import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faBrain,
    faCalendarDays,
    faCloud,
    faDatabase,
    faFileCircleQuestion,
    faFilters,
    faGavel,
    faLocationDot,
    faMagnifyingGlass,
    faServer,
    faUserShield,
} from '@fortawesome/pro-solid-svg-icons';
import { EUserGroup } from 'apiClient/Api';

/**
 * Gruppierung der Bereiche in der Seitennavigation. Bewusst grob gehalten — feinere
 * Gruppen bringen erst etwas, wenn eine Gruppe mehr als eine Bildschirmhöhe füllt.
 */
export type SettingsGroup = 'access' | 'services' | 'masterData';

/** Reihenfolge der Gruppen in der Navigation. */
export const SETTINGS_GROUP_ORDER: SettingsGroup[] = ['access', 'services', 'masterData'];

/**
 * Ein Bereich der Verwaltung.
 *
 * Neue Einstellungsseite hinzufügen = ein Eintrag hier plus eine Route in `App.tsx`.
 * Navigation, Übersichtskachel und Umleitung vom alten Pfad ergeben sich daraus von selbst.
 */
export interface SettingsSection {
    /**
     * Pfadsegment unter `/admin/settings`. Fehlt es, gibt es (noch) keine Detailseite —
     * der Bereich erscheint dann nur als Statuskachel in der Übersicht.
     */
    path?: string;

    /** Stabile Kennung; entspricht bei geprüften Bereichen dem `key` aus `GET /Settings/Status`. */
    key: string;

    /** i18n-Schlüssel des Namens. */
    labelKey: string;

    /** i18n-Schlüssel der Kurzbeschreibung für die Übersichtskachel. */
    descriptionKey: string;

    icon: IconDefinition;

    group: SettingsGroup;

    /** Sichtbar für Rollen bis einschließlich dieser Stufe (kleinerer Wert = mehr Rechte). */
    maxUserGroup: EUserGroup;

    /** Meldet der Server für diesen Bereich einen Zustand? */
    hasStatus?: boolean;

    /**
     * Früherer Pfad. Wird als dauerhafte Umleitung eingerichtet, damit gesetzte Lesezeichen
     * und verlinkte Anleitungen weiter funktionieren.
     */
    legacyPath?: string;
}

/**
 * Alle Bereiche der Verwaltung in Anzeigereihenfolge.
 *
 * Nicht enthalten sind Werkzeuge wie Audit-Log, Geodaten-Konverter oder die Generatoren:
 * das sind keine Einstellungen und bleiben eigene Menüpunkte.
 */
export const SETTINGS_SECTIONS: SettingsSection[] = [
    {
        path: 'ldap',
        key: 'ldap',
        labelKey: 'settings.sections.ldap.title',
        descriptionKey: 'settings.sections.ldap.description',
        icon: faServer,
        group: 'access',
        maxUserGroup: EUserGroup.ADMIN,
        hasStatus: true,
        legacyPath: '/admin/ldap',
    },
    {
        path: 'permissions',
        key: 'permissions',
        labelKey: 'settings.sections.permissions.title',
        descriptionKey: 'settings.sections.permissions.description',
        icon: faUserShield,
        group: 'access',
        maxUserGroup: EUserGroup.ADMIN,
        legacyPath: '/admin/permissions',
    },
    {
        path: 'ai',
        key: 'ai',
        labelKey: 'settings.sections.ai.title',
        descriptionKey: 'settings.sections.ai.description',
        icon: faBrain,
        group: 'services',
        maxUserGroup: EUserGroup.ADMIN,
        hasStatus: true,
    },
    {
        path: 'geocoding',
        key: 'geocoding',
        labelKey: 'settings.sections.geocoding.title',
        descriptionKey: 'settings.sections.geocoding.description',
        icon: faLocationDot,
        group: 'services',
        maxUserGroup: EUserGroup.ADMIN,
        hasStatus: true,
        legacyPath: '/admin/geocoding',
    },
    {
        path: 'labelconnect',
        key: 'labelconnect',
        labelKey: 'settings.sections.labelConnect.title',
        descriptionKey: 'settings.sections.labelConnect.description',
        icon: faCalendarDays,
        group: 'services',
        maxUserGroup: EUserGroup.ADMIN,
        hasStatus: true,
    },
    {
        path: 'o365',
        key: 'o365',
        labelKey: 'settings.sections.o365.title',
        descriptionKey: 'settings.sections.o365.description',
        icon: faCloud,
        group: 'services',
        maxUserGroup: EUserGroup.ADMIN,
        hasStatus: true,
    },
    {
        // Noch ohne Detailseite: die Embedding-Pipeline wird bisher über appsettings.json
        // konfiguriert. Der Zustand ist trotzdem hier sichtbar, weil ein nicht erreichbares
        // Ollama sonst erst auffällt, wenn die semantische Suche 503 liefert.
        key: 'embedding',
        labelKey: 'settings.sections.embedding.title',
        descriptionKey: 'settings.sections.embedding.description',
        icon: faMagnifyingGlass,
        group: 'services',
        maxUserGroup: EUserGroup.ADMIN,
        hasStatus: true,
    },
    {
        path: 'filetypes',
        key: 'filetypes',
        labelKey: 'settings.sections.fileTypes.title',
        descriptionKey: 'settings.sections.fileTypes.description',
        icon: faFileCircleQuestion,
        group: 'masterData',
        maxUserGroup: EUserGroup.ADMIN,
        legacyPath: '/admin/filetypes',
    },
    {
        path: 'systems',
        key: 'systems',
        labelKey: 'settings.sections.systems.title',
        descriptionKey: 'settings.sections.systems.description',
        icon: faDatabase,
        group: 'masterData',
        maxUserGroup: EUserGroup.ADMIN,
        legacyPath: '/admin/systems',
    },
    {
        path: 'filter-tags',
        key: 'filterTags',
        labelKey: 'settings.sections.filterTags.title',
        descriptionKey: 'settings.sections.filterTags.description',
        icon: faFilters,
        group: 'masterData',
        maxUserGroup: EUserGroup.ADMIN,
        legacyPath: '/admin/filterTags',
    },
    {
        path: 'terms',
        key: 'terms',
        labelKey: 'settings.sections.terms.title',
        descriptionKey: 'settings.sections.terms.description',
        icon: faGavel,
        group: 'masterData',
        maxUserGroup: EUserGroup.ADMIN,
        legacyPath: '/admin/terms',
    },
];

/** Basispfad der Verwaltung. */
export const SETTINGS_BASE_PATH = '/admin/settings';

/** Vollständiger Pfad eines Bereichs, oder `undefined`, wenn er keine Detailseite hat. */
export function settingsSectionPath(section: SettingsSection): string | undefined {
    return section.path ? `${SETTINGS_BASE_PATH}/${section.path}` : undefined;
}

/** Die für die angegebene Rolle sichtbaren Bereiche. */
export function visibleSettingsSections(userGroup: EUserGroup): SettingsSection[] {
    return SETTINGS_SECTIONS.filter((section) => userGroup <= section.maxUserGroup);
}
